/**
 * "Block time": what does this block, and when.
 *
 * Akash's three words (2026-11-02, decision G): Hold all tasks (a maintenance
 * window: nothing is dispatched at the site), Block lift from robot use, Allow
 * travel between floors. Only the two lift rules repeat: a maintenance window is
 * one period, by the api-server's own contract.
 *
 * Typed times rather than a drag. The old week grid armed a drag; on a horizontal
 * timeline a drag competes with scrolling, and a form is the one gesture a keyboard
 * can do too. The times are the SITE's clock (the lanes are drawn on it), a To
 * earlier than the From runs past midnight, and the period is read back in words
 * before anything is written.
 */
import { useEffect, useId, useState } from 'react';
import type { CreateLiftRuleWindowRequest, LiftRuleWindowKind } from '../../../api/v1';
import { HelpNote } from '../../../help';
import {
  ALLOW_TRAVEL_BETWEEN_FLOORS,
  BLOCK_LIFT_FROM_ROBOT_USE,
  cutOffSentence,
  HOLD_ALL_TASKS,
} from '../../Devices/lift-rules-words';
import { formatZonedDateTime, timeZoneLabel } from '../zoned-time';
import { addDays, blockRange, formatDuration, instantAt, weekdayOf, type DateKey } from './lane-time';
import type { LiftCalendar } from './useLiftCalendar';

type What = 'hold' | LiftRuleWindowKind;
type Repeat = 'none' | 'daily' | 'weekly' | 'weekdays';

const RRULE_DAY = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;
const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MAX_REPEATING_MS = 86_400_000;

function repeatRule(repeat: Repeat, date: DateKey): string | null {
  switch (repeat) {
    case 'none':
      return null;
    case 'daily':
      return 'FREQ=DAILY';
    case 'weekly':
      return `FREQ=WEEKLY;BYDAY=${RRULE_DAY[weekdayOf(date)]}`;
    case 'weekdays':
      return 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR';
  }
}

export interface BlockTimePreset {
  readonly lift?: string;
  readonly kind?: LiftRuleWindowKind;
}

export function BlockTimeDialog({
  day,
  timeZone,
  defaultFrom,
  onConfirm,
  onCancel,
  lifts,
  preset,
  bare = false,
  canHold = true,
}: {
  day: DateKey;
  timeZone: string;
  /** `HH:MM`. */
  defaultFrom: string;
  onConfirm: (range: { startMs: number; endMs: number; reason?: string }) => void;
  onCancel: () => void;
  /** Withheld: no lift choice is offered, for a role that may not set one or an
   *  api-server with no lift rules. */
  lifts?: LiftCalendar;
  preset?: BlockTimePreset | null;
  /** Inside a phone sheet, which has its own heading and its own Esc. */
  bare?: boolean;
  canHold?: boolean;
}) {
  const id = useId();
  const [date, setDate] = useState<DateKey>(day);
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(() => {
    const h = Number(defaultFrom.slice(0, 2));
    return `${String((h + 1) % 24).padStart(2, '0')}${defaultFrom.slice(2)}`;
  });
  const [reason, setReason] = useState('');
  const [what, setWhat] = useState<What>(() =>
    preset?.kind ?? (preset?.lift ? 'block_use' : canHold ? 'hold' : 'block_use'),
  );
  const [pickedLift, setPickedLift] = useState<Partial<Record<LiftRuleWindowKind, string>>>(() =>
    preset?.lift ? { block_use: preset.lift, allow_travel: preset.lift } : {},
  );
  const [repeat, setRepeat] = useState<Repeat>('none');
  const [repeatEnd, setRepeatEnd] = useState('');
  const [cutOff, setCutOff] = useState<readonly string[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const range = /^\d{4}-\d{2}-\d{2}$/.test(date) ? blockRange(date, from, to, timeZone) : null;

  useEffect(() => {
    if (bare) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, bare]);

  const isLift = what !== 'hold';
  const choices = isLift ? (lifts?.choices?.[what] ?? []) : [];
  const chosenLift = isLift
    ? pickedLift[what] && choices.includes(pickedLift[what]!)
      ? pickedLift[what]!
      : (choices[0] ?? null)
    : null;
  const tooLongToRepeat =
    isLift && repeat !== 'none' && range !== null && range.endMs - range.startMs > MAX_REPEATING_MS;
  const canSend = range !== null && !sending && (!isLift || chosenLift !== null) && !tooLongToRepeat;

  const sendLift = async (confirm: boolean) => {
    if (!range || !chosenLift || !lifts || what === 'hold') return;
    const r = reason.trim();
    const rule = repeatRule(repeat, date);
    const body: CreateLiftRuleWindowRequest = {
      lift: chosenLift,
      kind: what,
      starts_at_ms: range.startMs,
      duration_ms: range.endMs - range.startMs,
      timezone: timeZone,
      ...(rule ? { rrule: rule } : {}),
      // Through the whole of the last day it was asked to run on.
      ...(rule && /^\d{4}-\d{2}-\d{2}$/.test(repeatEnd)
        ? { repeat_until_ms: instantAt(addDays(repeatEnd, 1), 0, timeZone) - 1 }
        : {}),
      ...(r ? { reason: r } : {}),
      ...(confirm ? { confirm_cut_off: true } : {}),
    };
    setSending(true);
    setRefusal(null);
    const answer = await lifts.create(body);
    setSending(false);
    if (answer.ok) {
      onCancel();
    } else if (answer.cutOff) {
      setCutOff(answer.cutOff);
    } else {
      setCutOff(null);
      setRefusal(answer.words);
    }
  };

  if (cutOff) {
    return (
      <div
        className="nx-lanes__dialog"
        role="dialog"
        aria-modal="false"
        aria-labelledby={`${id}-t`}
        data-testid="lift-window-cutoff"
      >
        {!bare && <h3 id={`${id}-t`}>Block time</h3>}
        <p className="nx-lanes__dialog-when" data-testid="lift-window-cutoff-words">
          {cutOffSentence(cutOff, [], true)}
        </p>
        <div className="nx-lanes__dialog-actions">
          <button
            type="button"
            className="nx-lane-btn nx-lane-btn--primary"
            disabled={sending}
            onClick={() => void sendLift(true)}
            data-testid="lift-window-cutoff-confirm"
          >
            Block it
          </button>
          <button type="button" className="nx-lane-btn" onClick={() => setCutOff(null)}>
            Go back
          </button>
        </div>
        {refusal && (
          <p className="nx-lanes__dialog-error" role="alert">
            {refusal}
          </p>
        )}
      </div>
    );
  }

  const liftPicker = (kind: LiftRuleWindowKind) => {
    const list = lifts?.choices?.[kind] ?? [];
    if (what !== kind || list.length === 0) return null;
    return (
      <select
        aria-label="Which lift"
        value={chosenLift ?? ''}
        onChange={(e) => setPickedLift((p) => ({ ...p, [kind]: e.target.value }))}
        data-testid={`block-lift-${kind}`}
      >
        {list.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
    );
  };
  const noLiftWords = (kind: LiftRuleWindowKind): string | null => {
    if (!lifts) return null;
    if (lifts.choices === null) {
      return 'The robots’ system is not reporting its lifts just now, so this can’t be set.';
    }
    if (lifts.choices[kind].length > 0) return null;
    return kind === 'block_use'
      ? 'No lift here is set up for robots.'
      : 'No lift here can be opened for travel between floors: this site’s lifts already allow it, or it is built out of the map.';
  };
  const liftOption = (kind: LiftRuleWindowKind, title: string, effect: string) => {
    const none = noLiftWords(kind);
    return (
      <label
        className={`nx-block-what__opt${what === kind ? ' is-on' : ''}${none ? ' is-off' : ''}`}
        data-testid={`block-what-${kind}`}
      >
        <input
          type="radio"
          name={`${id}-what`}
          checked={what === kind}
          disabled={none !== null}
          onChange={() => setWhat(kind)}
        />
        <span className="nx-block-what__text">
          <strong>{title}</strong>
          <span>{none ?? effect}</span>
          {liftPicker(kind)}
        </span>
      </label>
    );
  };

  return (
    <div
      className="nx-lanes__dialog"
      role="dialog"
      aria-modal="false"
      aria-labelledby={`${id}-t`}
      data-testid="maint-confirm"
    >
      {!bare && (
        <h3 id={`${id}-t`}>{lifts ? 'Block time' : 'Block time — close the site for maintenance'}</h3>
      )}
      {lifts && <HelpNote id="tasks.block_time" />}
      {lifts && (
      <fieldset className="nx-block-what" aria-labelledby={`${id}-w`}>
        <legend id={`${id}-w`}>What does this block?</legend>
        {canHold && (
          <label
            className={`nx-block-what__opt${what === 'hold' ? ' is-on' : ''}`}
            data-testid="block-what-hold"
          >
            <input
              type="radio"
              name={`${id}-what`}
              checked={what === 'hold'}
              onChange={() => setWhat('hold')}
            />
            <span className="nx-block-what__text">
              <strong>{HOLD_ALL_TASKS}</strong>
              <span>
                No task will be dispatched during this period, and scheduled tasks that come due are
                recorded as skipped rather than run late. Work already running is left to finish, after
                which the robots dock and charge as usual.
              </span>
            </span>
          </label>
        )}
        <>
            {liftOption(
              'block_use',
              BLOCK_LIFT_FROM_ROBOT_USE,
              'Robots do not use this lift. A robot already riding finishes its ride first.',
            )}
            {liftOption(
              'allow_travel',
              ALLOW_TRAVEL_BETWEEN_FLOORS,
              'Robots may ride this lift between floors during this time.',
            )}
        </>
      </fieldset>
      )}
      <div className="nx-lanes__dialog-fields">
        <label>
          <span>Day</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="maint-day"
          />
        </label>
        <label>
          <span>From</span>
          <input
            type="time"
            value={from}
            step={900}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="maint-from"
          />
        </label>
        <label>
          <span>To</span>
          <input
            type="time"
            value={to}
            step={900}
            onChange={(e) => setTo(e.target.value)}
            data-testid="maint-to"
          />
        </label>
        {isLift && (
          <label>
            <span>Repeat</span>
            <select
              value={repeat}
              onChange={(e) => setRepeat(e.target.value as Repeat)}
              data-testid="block-repeat"
            >
              <option value="none">Does not repeat</option>
              <option value="daily">Every day</option>
              <option value="weekly">
                {`Every ${/^\d{4}-\d{2}-\d{2}$/.test(date) ? DAY_LONG[weekdayOf(date)] : 'week'}`}
              </option>
              <option value="weekdays">Monday to Friday</option>
            </select>
          </label>
        )}
        {isLift && repeat !== 'none' && (
          <label>
            <span>Until (optional)</span>
            <input
              type="date"
              value={repeatEnd}
              min={date}
              onChange={(e) => setRepeatEnd(e.target.value)}
              data-testid="block-repeat-until"
            />
          </label>
        )}
      </div>
      <p className="nx-lanes__dialog-when" data-testid="maint-when">
        {range
          ? `${formatZonedDateTime(range.startMs, timeZone)} → ${formatZonedDateTime(range.endMs, timeZone)} ${timeZoneLabel(timeZone, range.startMs)} · ${formatDuration(range.endMs - range.startMs)}`
          : 'Enter a day and two times.'}
      </p>
      {/* With lift rules off (or not this reader's) there is one thing to block,
          and the words sit here as they always did. */}
      {!lifts && (
        <p className="nx-lanes__dialog-effect">
          No task will be dispatched during this period, and scheduled tasks that come due are
          recorded as skipped rather than run late. Work already running is left to finish, after
          which the robots dock and charge as usual.
        </p>
      )}
      {tooLongToRepeat && (
        <p className="nx-lanes__dialog-error" role="alert">
          A repeating time can run for at most 24 hours each time.
        </p>
      )}
      <label className="nx-lanes__dialog-reason">
        <span>Reason (optional)</span>
        <input
          type="text"
          value={reason}
          maxLength={200}
          placeholder={isLift ? 'e.g. Ballroom event' : 'e.g. lift servicing, level 3'}
          onChange={(e) => setReason(e.target.value)}
          data-testid="maint-confirm-reason"
        />
      </label>
      <div className="nx-lanes__dialog-actions">
        <button
          type="button"
          className="nx-lane-btn nx-lane-btn--primary"
          disabled={!canSend}
          onClick={() => {
            if (!range) return;
            if (isLift) {
              void sendLift(false);
              return;
            }
            const r = reason.trim();
            onConfirm({ ...range, ...(r ? { reason: r } : {}) });
          }}
          data-testid="maint-confirm-ok"
        >
          {what === 'hold'
            ? 'Block this period'
            : what === 'block_use'
              ? BLOCK_LIFT_FROM_ROBOT_USE
              : ALLOW_TRAVEL_BETWEEN_FLOORS}
        </button>
        <button
          type="button"
          className="nx-lane-btn"
          onClick={onCancel}
          data-testid="maint-confirm-cancel"
        >
          Cancel
        </button>
      </div>
      {refusal && (
        <p className="nx-lanes__dialog-error" role="alert">
          {refusal}
        </p>
      )}
    </div>
  );
}
